import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import Spinner from '../../components/Spinner';
import './AdminOrderDetail.css';

function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/orders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order');
      }

      const data = await response.json();
      setOrder(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const updateOrderStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/orders/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      // Refresh order
      fetchOrder();
      showToast('Order status updated successfully', 'success');
    } catch (err) {
      showToast('Error updating order status: ' + err.message, 'error');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'processing': return 'status-processing';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      case 'refunded': return 'status-refunded';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="admin-order-detail">
        <Spinner text="Loading order..." />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="admin-order-detail">
        <div className="error-message">Error: {error || 'Order not found'}</div>
        <Link to="/admin/orders" className="back-button">Back to Orders</Link>
      </div>
    );
  }

  return (
    <div className="admin-order-detail">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="admin-header">
        <h1>ORDER #{order.id}</h1>
        <Link to="/admin/orders" className="back-button">Back to Orders</Link>
      </div>

      <div className="order-detail-grid">
        <div className="order-info-section">
          <div className="info-card">
            <h2>ORDER INFORMATION</h2>
            <div className="info-row">
              <span className="label">Order ID:</span>
              <span className="value">#{order.id}</span>
            </div>
            <div className="info-row">
              <span className="label">Date:</span>
              <span className="value">{formatDate(order.created_at)}</span>
            </div>
            <div className="info-row">
              <span className="label">Status:</span>
              <span className={`status-badge ${getStatusClass(order.status)}`}>
                {order.status}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Payment Method:</span>
              <span className="value">{order.payment_method || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Total:</span>
              <span className="value total">{formatPrice(order.total)}</span>
            </div>
          </div>

          <div className="info-card">
            <h2>CUSTOMER INFORMATION</h2>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{order.customer_name || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{order.customer_email || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Phone:</span>
              <span className="value">{order.customer_phone || 'N/A'}</span>
            </div>
          </div>

          <div className="info-card">
            <h2>SHIPPING ADDRESS</h2>
            <div className="address-text">
              {order.shipping_address ? (
                <>
                  {order.shipping_address.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </>
              ) : (
                'N/A'
              )}
            </div>
          </div>

          <div className="info-card">
            <h2>UPDATE STATUS</h2>
            <select
              value={order.status}
              onChange={(e) => updateOrderStatus(e.target.value)}
              className="status-update-select"
            >
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        <div className="order-items-section">
          <div className="items-card">
            <h2>ORDER ITEMS</h2>
            {order.items && order.items.length > 0 ? (
              <div className="items-list">
                {order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-info">
                      <div className="item-name">{item.product_name || 'Unknown Product'}</div>
                      {(item.color || item.size) && (
                        <div className="item-variation">
                          {item.color && <span>Color: {item.color}</span>}
                          {item.color && item.size && <span> | </span>}
                          {item.size && <span>Size: {item.size}</span>}
                        </div>
                      )}
                    </div>
                    <div className="item-quantity">Qty: {item.quantity}</div>
                    <div className="item-price">{formatPrice(item.price)}</div>
                    <div className="item-total">{formatPrice(item.quantity * item.price)}</div>
                  </div>
                ))}

                <div className="order-totals">
                  <div className="total-row">
                    <span className="total-label">Subtotal:</span>
                    <span className="total-value">{formatPrice(order.subtotal || order.total)}</span>
                  </div>
                  {order.shipping_cost > 0 && (
                    <div className="total-row">
                      <span className="total-label">Shipping:</span>
                      <span className="total-value">{formatPrice(order.shipping_cost)}</span>
                    </div>
                  )}
                  {order.tax > 0 && (
                    <div className="total-row">
                      <span className="total-label">Tax:</span>
                      <span className="total-value">{formatPrice(order.tax)}</span>
                    </div>
                  )}
                  <div className="total-row grand-total">
                    <span className="total-label">Total:</span>
                    <span className="total-value">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-items">No items in this order.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminOrderDetail;
